import { motion } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { BRAND } from "@/content/site";
import { useLang } from "@/lang";

const EASE = [0.22, 1, 0.36, 1];

export default function ServiceArea() {
  const { t } = useLang();
  const vw = { once: true, amount: 0.3 };

  return (
    <section id="service-area" data-testid="service-area-section" className="bg-cream-2 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE }} className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.area.eyebrow}</p>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }} className="font-serif text-4xl font-medium leading-[1.1] text-navy sm:text-5xl">
            {t.area.title}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE, delay: 0.2 }} className="mt-5 max-w-md text-base leading-relaxed text-mutedink sm:text-lg">
            {t.area.lead}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE, delay: 0.3 }} className="mt-8 inline-flex flex-wrap items-center gap-3 rounded-2xl border border-gold/40 bg-white px-5 py-4">
            <Phone className="h-4 w-4 shrink-0 text-gold" />
            <p className="text-sm text-navy/80">{t.area.note}</p>
            <a href={BRAND.phoneHref} data-testid="area-note-call" className="text-sm font-semibold uppercase tracking-[0.12em] text-gold underline-offset-4 hover:underline">
              {BRAND.phone}
            </a>
          </motion.div>
        </div>

        <div className="divide-y divide-hairline border-y border-hairline">
          {t.area.areas.map((a, i) => (
            <motion.div
              key={a}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.06 }}
              data-testid={`area-row-${i + 1}`}
              className="group flex items-center justify-between gap-4 py-4 sm:py-5"
            >
              <div className="flex items-baseline gap-5">
                <span className="font-serif text-sm italic text-gold">0{i + 1}</span>
                <span className="font-serif text-2xl text-navy transition-transform duration-300 group-hover:translate-x-1.5 sm:text-3xl">
                  {a}
                </span>
              </div>
              <MapPin className="h-4 w-4 shrink-0 text-gold/70 transition-colors duration-300 group-hover:text-gold" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
